/* GENERATED from the tested ERS recurring-program recommendation engine. */
window.ERSRecurringProgram = (function createRecurringProgramEngine() {
  const DAYS_PER_MONTH = 30.4375;
  const DAY_MS = 86_400_000;
  const uncertaintyDays = Object.freeze({ low: -2, standard: 0, high: 7 });

  function assertInput(condition, message) {
    if (!condition) throw new TypeError(message);
  }

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function interpolate(left, right, fraction) {
    return left + (right - left) * fraction;
  }

  function round(value, places = 0) {
    const scale = 10 ** places;
    return Math.round((value + Number.EPSILON) * scale) / scale;
  }

  function isoAtDay(asOfDate, day) {
    const time = Date.parse(`${asOfDate}T00:00:00Z`) + day * DAY_MS;
    if (!Number.isFinite(time) || Math.abs(time) > 8.64e15) return null;
    const date = new Date(time);
    return Number.isNaN(date.valueOf()) ? null : date.toISOString().slice(0, 10);
  }

  function comparableAt(model, requestedMonthlyPairs) {
    const knots = model.priorKnots;
    const minimum = model.support.minimumMonthlyPairs;
    const maximum = model.support.maximumMonthlyPairs;
    const matched = clamp(requestedMonthlyPairs, minimum, maximum);
    let rightIndex = knots.findIndex((knot) => knot.usageMonthlyPairs >= matched);
    if (rightIndex < 0) rightIndex = knots.length - 1;
    const leftIndex = Math.max(0, rightIndex - 1);
    const left = knots[leftIndex];
    const right = knots[rightIndex];
    const logSpan = Math.log(right.usageMonthlyPairs) - Math.log(left.usageMonthlyPairs);
    const fraction = logSpan > 0 ? (Math.log(matched) - Math.log(left.usageMonthlyPairs)) / logSpan : 0;
    const mix = (field) => interpolate(left[field], right[field], fraction);
    const mixRange = (field, part) => interpolate(left[field][part], right[field][part], fraction);
    return {
      baselineMonthlyPairs: round(requestedMonthlyPairs),
      matchedMonthlyPairs: round(matched),
      supportStatus: requestedMonthlyPairs < minimum ? 'below-supported-range' : requestedMonthlyPairs > maximum ? 'above-supported-range' : 'within-supported-range',
      usagePercentile: round(mix('usagePercentile'), 1),
      distinctPrograms: Math.round(mix('distinctPrograms')),
      coverageWeeks: {
        q25: round(mixRange('coverageWeeks', 'q25'), 1),
        median: round(mixRange('coverageWeeks', 'median'), 1),
        q75: round(mixRange('coverageWeeks', 'q75'), 1)
      },
      typicalLotPairs: Math.round(mix('lotPairsMedian')),
      cadenceDays: Math.round(mix('cadenceDaysMedian'))
    };
  }

  function projectPhysicalInventory(day, dailyDemand, currentInventoryPairs, events) {
    let inventoryPairs = currentInventoryPairs;
    let unmetDemandPairs = 0;
    let cursorDay = 0;
    const eligibleEvents = events
      .filter((event) => event.pairs > 0 && event.day >= 0 && event.day <= day)
      .sort((a, b) => a.day - b.day || a.type.localeCompare(b.type));
    const consume = (durationDays) => {
      const demand = dailyDemand * durationDays;
      const supplied = Math.min(inventoryPairs, demand);
      inventoryPairs -= supplied;
      unmetDemandPairs += demand - supplied;
    };
    for (const event of eligibleEvents) {
      consume(event.day - cursorDay);
      inventoryPairs += event.pairs;
      cursorDay = event.day;
    }
    consume(day - cursorDay);
    return { inventoryPairs, unmetDemandPairs };
  }

  function inboundEvents(inboundPairs, inboundArrivalDays) {
    return inboundPairs > 0 && Number.isFinite(inboundArrivalDays)
      ? [{ type: 'existing-inbound-arrival', day: inboundArrivalDays, pairs: inboundPairs }]
      : [];
  }

  function chooseDecisionDay({ currentInventoryPairs, inboundPairs, inboundArrivalDays, dailyDemand, leadDays, protectionPairs }) {
    const retainsProtectionToInbound = inboundPairs <= 0
      || currentInventoryPairs - dailyDemand * inboundArrivalDays >= protectionPairs;
    let timingInboundPairs = inboundPairs > 0 && retainsProtectionToInbound ? inboundPairs : 0;
    let candidate = Math.floor((currentInventoryPairs + timingInboundPairs - protectionPairs) / dailyDemand - leadDays);
    candidate = Math.max(0, candidate);
    if (timingInboundPairs > 0 && inboundArrivalDays > candidate + leadDays) {
      timingInboundPairs = 0;
      candidate = Math.max(0, Math.floor((currentInventoryPairs - protectionPairs) / dailyDemand - leadDays));
    }
    const timingEvents = inboundEvents(timingInboundPairs, inboundArrivalDays);
    while (candidate > 0) {
      const projection = projectPhysicalInventory(candidate + leadDays, dailyDemand, currentInventoryPairs, timingEvents);
      if (projection.inventoryPairs >= protectionPairs && projection.unmetDemandPairs === 0) break;
      candidate -= 1;
    }
    return candidate;
  }

  function trajectoryFor({ asOfDate, currentInventoryPairs, inboundPairs, inboundArrivalDays, dailyDemand, decisionDay, leadDays, recommendedPairs, protectionPairs, cycleStockDays, lateInboundCredited }) {
    const arrivalDay = decisionDay + leadDays;
    const ordinaryHorizonDay = Math.ceil(arrivalDay + clamp(cycleStockDays * 0.25, 14, 28));
    const horizonDay = lateInboundCredited ? Math.max(ordinaryHorizonDay, inboundArrivalDays) : ordinaryHorizonDay;
    const inventoryEvents = [
      ...inboundEvents(inboundPairs, inboundArrivalDays),
      { type: 'recommended-replenishment-arrival', day: arrivalDay, pairs: recommendedPairs }
    ].filter((event) => event.day <= horizonDay)
      .sort((a, b) => a.day - b.day || a.type.localeCompare(b.type));
    const eventDays = new Set([0, decisionDay, arrivalDay, horizonDay, ...inventoryEvents.map((event) => event.day)]);
    const points = [];
    let inventoryPairs = currentInventoryPairs;
    let cursorDay = 0;
    for (const day of [...eventDays].sort((a, b) => a - b)) {
      inventoryPairs = Math.max(0, inventoryPairs - dailyDemand * (day - cursorDay));
      cursorDay = day;
      const dayEvents = inventoryEvents.filter((event) => event.day === day);
      points.push({
        day,
        date: isoAtDay(asOfDate, day),
        inventoryPairs: round(inventoryPairs),
        kind: day === 0 ? 'current' : day === decisionDay ? 'reorder-decision' : dayEvents.length ? 'before-event' : 'projection'
      });
      for (const event of dayEvents) {
        inventoryPairs += event.pairs;
        points.push({
          day,
          date: isoAtDay(asOfDate, day),
          inventoryPairs: round(inventoryPairs),
          kind: event.type
        });
      }
    }
    return {
      horizonDays: horizonDay,
      protectionPairs: round(protectionPairs),
      points,
      events: [
        { type: 'reorder-decision', day: decisionDay, date: isoAtDay(asOfDate, decisionDay) },
        ...(inboundPairs > 0 && inboundArrivalDays <= horizonDay ? [{ type: 'existing-inbound-arrival', day: inboundArrivalDays, date: isoAtDay(asOfDate, inboundArrivalDays), pairs: round(inboundPairs) }] : []),
        { type: 'recommended-replenishment-arrival', day: arrivalDay, date: isoAtDay(asOfDate, arrivalDay), pairs: recommendedPairs }
      ].sort((a, b) => a.day - b.day || a.type.localeCompare(b.type))
    };
  }

  function evaluate(model, inputs, commercial) {
    assertInput(model?.schemaVersion === '1.0.0' && Array.isArray(model.priorKnots) && model.priorKnots.length >= 2, 'A valid ERS recurring-program aggregate is required.');
    assertInput(Number.isFinite(inputs?.baselineMonthlyPairs) && inputs.baselineMonthlyPairs > 0, 'baselineMonthlyPairs must be positive.');
    assertInput(Number.isFinite(inputs?.currentInventoryPairs) && inputs.currentInventoryPairs >= 0, 'currentInventoryPairs must be nonnegative.');
    assertInput(/^\d{4}-\d{2}-\d{2}$/.test(inputs?.asOfDate ?? '') && Number.isFinite(Date.parse(`${inputs.asOfDate}T00:00:00Z`)), 'asOfDate must be an ISO calendar date.');
    assertInput(Number.isInteger(commercial?.pairsPerCase) && commercial.pairsPerCase > 0, 'pairsPerCase must be a positive integer current-program value.');
    assertInput(Number.isInteger(commercial?.minimumCases) && commercial.minimumCases > 0, 'minimumCases must be a positive integer current-program value.');
    assertInput(Number.isFinite(commercial?.productionDays) && commercial.productionDays >= 0, 'productionDays must be a nonnegative current-program value.');

    const baselineMonthlyPairs = inputs.baselineMonthlyPairs;
    const currentInventoryPairs = inputs.currentInventoryPairs;
    const inboundPairs = Number.isFinite(inputs.inboundPairs) ? Math.max(0, inputs.inboundPairs) : 0;
    const inboundArrivalDays = inboundPairs > 0 && Number.isFinite(inputs.inboundArrivalDays) ? Math.max(0, Math.round(inputs.inboundArrivalDays)) : Number.POSITIVE_INFINITY;
    const transitDays = Number.isFinite(inputs.transitDays) ? Math.max(0, inputs.transitDays) : 0;
    const seasonalChangePercent = Number.isFinite(inputs.seasonalChangePercent) ? clamp(inputs.seasonalChangePercent, -50, 100) : 0;
    const uncertainty = Object.hasOwn(uncertaintyDays, inputs.uncertainty) ? inputs.uncertainty : 'standard';
    const comparable = comparableAt(model, baselineMonthlyPairs);
    const futureMonthlyPairs = baselineMonthlyPairs * (1 + seasonalChangePercent / 100);
    const futureDailyPairs = futureMonthlyPairs / DAYS_PER_MONTH;
    const ordinaryProtectionDays = model.referenceConditions.ordinaryProtectionDays;
    const incrementalProtectionDays = uncertaintyDays[uncertainty];
    const protectionDays = Math.max(2, ordinaryProtectionDays + incrementalProtectionDays);
    const comparableCoverageDays = comparable.coverageWeeks.median * 7;
    const cycleStockDays = Math.max(21, comparableCoverageDays - ordinaryProtectionDays);
    const leadDays = Math.round(commercial.productionDays + transitDays);
    const protectionPairs = futureDailyPairs * protectionDays;
    const decisionDay = chooseDecisionDay({ currentInventoryPairs, inboundPairs, inboundArrivalDays, dailyDemand: futureDailyPairs, leadDays, protectionPairs });
    const arrivalDay = decisionDay + leadDays;
    const existingInboundEvents = inboundEvents(inboundPairs, inboundArrivalDays);
    const decisionProjection = projectPhysicalInventory(decisionDay, futureDailyPairs, currentInventoryPairs, existingInboundEvents);
    const arrivalProjection = projectPhysicalInventory(arrivalDay, futureDailyPairs, currentInventoryPairs, existingInboundEvents);
    const inventoryAtDecisionPairs = decisionProjection.inventoryPairs;
    const inventoryBeforeArrivalPairs = arrivalProjection.inventoryPairs;
    const targetAfterArrivalPairs = futureDailyPairs * (cycleStockDays + protectionDays);
    const lateInboundCredited = inboundPairs > 0
      && inboundArrivalDays > arrivalDay
      && inboundArrivalDays <= arrivalDay + cycleStockDays;
    const targetPositionNeedPairs = targetAfterArrivalPairs - inventoryBeforeArrivalPairs - (lateInboundCredited ? inboundPairs : 0);
    const bridgeToLateInboundNeedPairs = lateInboundCredited
      ? protectionPairs + futureDailyPairs * (inboundArrivalDays - arrivalDay) - inventoryBeforeArrivalPairs
      : 0;
    const replenishmentNeedPairs = Math.max(0, targetPositionNeedPairs, bridgeToLateInboundNeedPairs);
    const noLateInboundNeedPairs = Math.max(0, targetAfterArrivalPairs - inventoryBeforeArrivalPairs);
    const lateInboundLotCreditPairs = Math.max(0, noLateInboundNeedPairs - replenishmentNeedPairs);
    const minimumPairs = commercial.pairsPerCase * commercial.minimumCases;
    const recommendedCases = Math.max(commercial.minimumCases, Math.ceil(replenishmentNeedPairs / commercial.pairsPerCase));
    const recommendedPairs = recommendedCases * commercial.pairsPerCase;
    const projectedAfterArrivalPairs = inventoryBeforeArrivalPairs + recommendedPairs;
    const baselineCycleStockPairs = baselineMonthlyPairs / DAYS_PER_MONTH * cycleStockDays;
    const inboundBeforeDecision = inboundPairs > 0 && inboundArrivalDays <= decisionDay ? inboundPairs : 0;
    const inboundBeforeArrival = inboundPairs > 0 && inboundArrivalDays <= arrivalDay ? inboundPairs : 0;

    return {
      schemaVersion: '1.0.0',
      comparable,
      baseline: {
        comparableCoverageDays: round(comparableCoverageDays, 1),
        ordinaryProtectionDays,
        cycleStockDays: round(cycleStockDays, 1),
        cycleStockPairs: round(baselineCycleStockPairs)
      },
      conditions: {
        baselineMonthlyPairs: round(baselineMonthlyPairs),
        futureMonthlyPairs: round(futureMonthlyPairs),
        futureDailyPairs: round(futureDailyPairs, 1),
        currentInventoryPairs: round(currentInventoryPairs),
        inboundPairs: round(inboundPairs),
        inboundArrivalDays: Number.isFinite(inboundArrivalDays) ? inboundArrivalDays : null,
        productionDays: commercial.productionDays,
        transitDays,
        effectiveLeadDays: leadDays,
        seasonalChangePercent,
        uncertainty
      },
      adjustments: {
        futureDemandMonthlyDeltaPairs: round(futureMonthlyPairs - baselineMonthlyPairs),
        incrementalProtectionDays,
        protectionDays,
        protectionPairs: round(protectionPairs),
        inboundPairsAppliedByDecision: round(inboundBeforeDecision),
        inboundPairsAppliedByReplenishment: round(inboundBeforeArrival),
        lateInboundPairsCreditedToLot: round(lateInboundLotCreditPairs)
      },
      decision: {
        status: decisionDay === 0 ? 'order-now' : 'future-reorder',
        currentOnHandDays: round(currentInventoryPairs / (baselineMonthlyPairs / DAYS_PER_MONTH), 1),
        reorderInDays: decisionDay,
        reorderDate: isoAtDay(inputs.asOfDate, decisionDay),
        projectedInventoryAtDecisionPairs: round(inventoryAtDecisionPairs),
        replenishmentArrivalInDays: arrivalDay,
        replenishmentArrivalDate: isoAtDay(inputs.asOfDate, arrivalDay),
        projectedInventoryBeforeArrivalPairs: round(inventoryBeforeArrivalPairs),
        uncoveredDemandBeforeArrivalPairs: round(arrivalProjection.unmetDemandPairs),
        recommendedCases,
        recommendedPairs,
        currentMinimumPairs: minimumPairs,
        projectedInventoryAfterArrivalPairs: round(projectedAfterArrivalPairs),
        projectedPostArrivalCoverageDays: round(projectedAfterArrivalPairs / futureDailyPairs, 1),
        safetyProtectionDays: protectionDays,
        safetyProtectionPairs: round(protectionPairs)
      },
      trajectory: trajectoryFor({
        asOfDate: inputs.asOfDate,
        currentInventoryPairs,
        inboundPairs,
        inboundArrivalDays,
        dailyDemand: futureDailyPairs,
        decisionDay,
        leadDays,
        recommendedPairs,
        protectionPairs,
        cycleStockDays,
        lateInboundCredited: lateInboundLotCreditPairs > 0
      })
    };
  }

  return Object.freeze({ evaluate });
})();
